// "Raise invoices" (BUILD_SPEC.md §8.4 step 4). Plan.md G23. The uninvoiced-
// dispatch queue (`GET /invoicing/uninvoiced`) grouped by customer -> select
// customer(s) (+ a shared period, required only when any selected customer is
// CONSOLIDATED — `POST /invoicing/raise` takes one shared periodFrom/periodTo per
// call, per packages/core/src/schemas/invoicing.ts's `raiseInvoicesSchema`) ->
// raise -> shows the created draft invoices.
import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Checkbox, Group, Paper, Stack, Table, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconAlertCircle, IconCheck, IconReceipt2 } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useCanSell } from '../../auth/permissions';
import { sastDayEnd, sastDayStart, todayIsoDate } from '../../lib/sastDate';
import { formatDate, formatQty } from './format';
import { useRaiseInvoices, useUninvoiced } from './api';
import { ProductName } from './components/EntityNames';

export function RaiseInvoices() {
  const canSell = useCanSell();
  const { data, isLoading, isError } = useUninvoiced();
  const raiseInvoices = useRaiseInvoices();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState(todayIsoDate());

  const groups = data ?? [];
  const selectedGroups = groups.filter((g) => selected.has(g.customerId));
  const anyConsolidatedSelected = selectedGroups.some((g) => g.invoicingMode === 'CONSOLIDATED');
  const periodValid = !anyConsolidatedSelected || (periodFrom !== '' && periodTo !== '' && periodFrom <= periodTo);
  const canRaise = selected.size > 0 && periodValid;

  function toggle(customerId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  }

  async function handleRaise() {
    await raiseInvoices.mutateAsync({
      customerIds: [...selected],
      // Business-day timezone (spec §4 item 7, Amendment 2026-07-10): `periodFrom`/
      // `periodTo` are `YYYY-MM-DD` strings from `type="date"` inputs; a bare
      // `new Date(periodFrom)` would parse them as UTC midnight — 02:00 SAST, so a
      // load dispatched 00:00-02:00 SAST on the period's first day would silently
      // fall outside its own consolidation period. `sastDayStart`/`sastDayEnd`
      // (lib/sastDate.ts) produce the unambiguous SAST day-boundary instants
      // regardless of where the server runs.
      periodFrom: anyConsolidatedSelected && periodFrom ? sastDayStart(periodFrom) : undefined,
      periodTo: anyConsolidatedSelected && periodTo ? sastDayEnd(periodTo) : undefined,
    });
    setSelected(new Set());
  }

  const totalLoads = useMemo(() => groups.reduce((sum, g) => sum + g.dispatches.length, 0), [groups]);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Title order={2}>Raise invoices</Title>
          <Text c="dimmed">
            {isLoading ? 'Loading uninvoiced dispatches…' : `${totalLoads} uninvoiced load(s) across ${groups.length} customer(s).`}
          </Text>
        </Stack>
      </Group>

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load the uninvoiced queue.
        </Alert>
      )}

      {raiseInvoices.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {raiseInvoices.error instanceof ApiRequestError ? raiseInvoices.error.message : 'Failed to raise invoices.'}
        </Alert>
      )}

      {raiseInvoices.isSuccess && (
        <Alert color="green" icon={<IconCheck size={16} />} title={raiseInvoices.data.summary}>
          <Stack gap={4}>
            {raiseInvoices.data.invoices.map((inv) => (
              <Group key={inv.id} justify="space-between">
                <Text size="sm">{inv.number}</Text>
                {/* A bare `Link` (not Mantine's `component={Link}` polymorphism) so
                    TanStack Router's `params` type is inferred correctly against the
                    target route — see features/sales-invoicing/DispatchWizard.tsx's
                    identical comment for the full precedent. */}
                <Link to="/sales-invoicing/invoices/$invoiceId" params={{ invoiceId: inv.id }}>
                  <Button component="span" size="xs" variant="light">
                    View invoice
                  </Button>
                </Link>
              </Group>
            ))}
          </Stack>
        </Alert>
      )}

      {!isLoading && groups.length === 0 && (
        <Text c="dimmed" ta="center" py="lg">
          Nothing to invoice — every POSTED dispatch has already been billed.
        </Text>
      )}

      {groups.map((group) => (
        <Paper key={group.customerId} withBorder radius="md" p="md">
          <Group justify="space-between" mb="sm">
            <Group>
              <Checkbox checked={selected.has(group.customerId)} onChange={() => toggle(group.customerId)} label={<Text fw={600}>{group.customerName}</Text>} />
              <Badge color={group.invoicingMode === 'PER_LOAD' ? 'blue' : 'grape'} variant="light">
                {group.invoicingMode}
                {group.consolidationPeriod ? ` · ${group.consolidationPeriod}` : ''}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {group.dispatches.length} load(s)
            </Text>
          </Group>
          <Table.ScrollContainer minWidth={640}>
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Dispatch</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Qty</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {group.dispatches.flatMap((d) =>
                  d.lines.map((line) => (
                    <Table.Tr key={line.dispatchLineId}>
                      <Table.Td>{d.number}</Table.Td>
                      <Table.Td>{formatDate(d.dispatchedAt)}</Table.Td>
                      <Table.Td>
                        <ProductName id={line.productId} />
                      </Table.Td>
                      <Table.Td>{formatQty(line.qty)}</Table.Td>
                    </Table.Tr>
                  )),
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      ))}

      {groups.length > 0 && (
        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            {anyConsolidatedSelected && (
              <Group grow>
                <TextInput
                  type="date"
                  label="Period from"
                  description="Dispatches from this date onward are grouped into one invoice per consolidated customer."
                  required
                  value={periodFrom}
                  onChange={(e) => setPeriodFrom(e.currentTarget.value)}
                />
                <TextInput
                  type="date"
                  label="Period to"
                  description="The last day included in the consolidated invoice period."
                  required
                  value={periodTo}
                  onChange={(e) => setPeriodTo(e.currentTarget.value)}
                />
              </Group>
            )}
            <Group justify="flex-end">
              <Tooltip
                label={
                  canSell
                    ? "Creates draft invoices for the selected customers' uninvoiced loads — you still need to Approve each one afterwards."
                    : 'Requires Sales & Accounts role'
                }
              >
                <span>
                  <Button
                    leftSection={<IconReceipt2 size={16} />}
                    disabled={!canSell || !canRaise}
                    loading={raiseInvoices.isPending}
                    onClick={handleRaise}
                  >
                    Raise invoice(s) for {selected.size} customer(s)
                  </Button>
                </span>
              </Tooltip>
            </Group>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
